import type { ExpandedState } from '@tanstack/react-table';
import type { HeaderConfig, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { Spinner } from '@tempo-io/sui-spinner';
import { addMonths, format } from 'date-fns';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomField } from '@/lib/api/types/customField';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
  IVisualizationHeader,
} from '@/lib/api/types/loop-visualization';
import type { Team } from '@/lib/api/types/teams';
import { useUpdateVisualizationItemMutation } from '@/lib/mutations/loop-visualization/items/useUpdateVisualizationItemMutation';
import { useUpsertTimelineLayoutMutation } from '@/lib/mutations/loop-visualization/useTimelineLayoutMutations';
import { useGetCustomFieldsQuery } from '@/lib/queries/common/useGetCustomFieldsQuery';
import {
  useTimelineHeaderConfig,
  useTimelineSubHeaderConfigs,
} from '@/lib/queries/loop-visualization/useTimelineHeaderConfig';
import { useVisualizationItemRanks } from '@/lib/queries/loop-visualization/useVisualizationItemRanks';
import { toISOTimestamp } from '@/lib/utils/common/dateFormats';
import { DEFAULT_FALLBACK_COLOR } from '@/lib/utils/loop-visualization/colors';
import { headerToPropertySelector } from '@/lib/utils/loop-visualization/commonLayoutUtils';
import { getItemFieldValue } from '@/lib/utils/loop-visualization/fieldExtraction';
import { isCustomFieldApplicableToItemType } from '@/lib/utils/loop-visualization/isCustomFieldApplicableToItemType';
import {
  applyHeaderValueToUpdate,
  createEmptyUpdateInput,
} from '@/lib/utils/loop-visualization/visualizationItemUpdate';
import { VisualizationNoHeadersState } from '../empty-state/VisualizationNoHeadersState';
import { VisualizationNoItemsMatchFiltersState } from '../empty-state/VisualizationNoItemsMatchFiltersState';
import { VisualizationNoItemsMatchLayoutState } from '../empty-state/VisualizationNoItemsMatchLayoutState';
import { VisualizationNoItemsState } from '../empty-state/VisualizationNoItemsState';
import { Legend } from '../published/Legend';
import { COMPOSITE_ID_SEPARATOR, NO_VALUE_ID } from '../utils/constants';
import { useRowHeaderWidth } from '../utils/useRowHeaderWidth';
import { useVisualizationHeaderOptions } from '../utils/useVisualizationHeaderOptions';
import { getHeaderFieldId, getHeaderItemKey } from '../utils/visualizationHeaderUtils';
import { useViewContext } from '../ViewContext';
import { TimelineMinimap } from './minimap/TimelineMinimap';
import { ReorderTimelineHeadersDialog } from './ReorderTimelineHeadersDialog';
import { TimelineAddHeaderMenu } from './TimelineAddHeaderMenu';
import { TimelineNoItemsWithDates } from './TimelineNoItemsWithDates';
import { TimelineTable } from './TimelineTable';
import { useTimelineItemsWithChildren } from './useFilteredTimelineItems';
import { useTimelineColorContext } from './useTimelineColorContext';
import { useTimelineMainHeaders, useTimelineSubHeaders } from './useTimelineHeaderColors';
import { useTimelineItemLevels } from './useTimelineItemLevels';
import { useTimelineItemTypeFilter } from './useTimelineItemTypeFilter';
import { useTimelineMinimapDates } from './useTimelineMinimapDates';
import type { TimelineRowType } from './useTimelineTableColumns';
import { useTimelineTableData } from './useTimelineTableData';

export interface TimelineProps {
  items: ItemV2[];
  allItemsById?: ReadonlyMap<number, Partial<ItemV2>>;
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  layout: TimelineLayoutDTO;
  visualizationId: number;
  hasFilters: boolean;
  from: string | null;
  to: string | null;
  onDateChange: (dates: { from: string; to: string }) => void;
  canEdit: boolean;
  viewLocked?: boolean;
  isPublishedMode?: boolean;
  isExportMode?: boolean;
  publishedLegendItems?: { color: string; label: string; isHidden?: boolean }[];
  publishedItemRanks?: Map<number, string>;
  mainHeaderConfig?: HeaderConfig;
  subHeaderConfigs?: Record<string, HeaderConfig>;
  teamsOverride?: Team[];
  onItemClick?: (itemId: number) => void;
  hideLegendOverlay?: boolean;
  isFetching?: boolean;
}

export const Timeline = ({
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  layout,
  visualizationId,
  hasFilters,
  from,
  to,
  onDateChange,
  canEdit,
  viewLocked: _viewLocked = false,
  isPublishedMode = false,
  isExportMode = false,
  publishedLegendItems,
  mainHeaderConfig: mainHeaderConfigProp,
  subHeaderConfigs: subHeaderConfigsProp,
  teamsOverride,
  publishedItemRanks,
  isFetching,
  onItemClick,
  hideLegendOverlay = false,
  allItemsById,
}: TimelineProps) => {
  const { t } = useTranslation();
  const { viewId } = useViewContext();
  const [expandedRows, setExpandedRows] = useState<ExpandedState>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [expandedChildren, setExpandedChildren] = useState<Record<string, boolean>>({});
  const [reorderHeadersModalState, setReorderHeadersModalState] = useState<{
    isReorderingMainHeader: boolean;
    parentHeaderId: string | null;
  } | null>(null);

  const { mutate: upsertLayout } = useUpsertTimelineLayoutMutation(viewId, visualizationId);
  const { mutate: updateVisualizationItem } = useUpdateVisualizationItemMutation();
  const { data: customFields = [] } = useGetCustomFieldsQuery({
    enabled: canEdit && !isPublishedMode,
  });
  const customFieldsById = useMemo(
    () => new Map<number, CustomField>(customFields.map((f) => [f.id, f])),
    [customFields],
  );
  const { data: fetchedItemRanks } = useVisualizationItemRanks({
    viewId,
    visualizationId,
    enabled: !isPublishedMode || publishedItemRanks === undefined,
  });
  const itemRanks = publishedItemRanks ?? fetchedItemRanks;

  const { rowHeaderWidth, setRowHeaderWidth } = useRowHeaderWidth({
    layoutWidth: layout.rowHeaderWidth,
    defaultWidth: 120,
    mutate: upsertLayout,
  });

  const { itemTypeFilter } = useTimelineItemTypeFilter(layout.itemLevelId, !isPublishedMode);

  const effectiveFrom = from ?? layout.startDate ?? null;
  const effectiveTo = to ?? layout.endDate ?? null;

  useEffect(() => {
    if (from !== null && to !== null) return;
    const today = new Date();
    onDateChange({
      from: layout.startDate ?? format(today, 'yyyy-MM-dd'),
      to: layout.endDate ?? format(addMonths(today, 6), 'yyyy-MM-dd'),
    });
  }, [from, to, onDateChange, layout.startDate, layout.endDate]);

  const { curLevelItemTypes, nextLevelItemTypes } = useTimelineItemLevels({
    itemLevelId: layout.itemLevelId,
    itemTypes,
    itemLevels,
  });

  const filteredItemsWithChildren = useTimelineItemsWithChildren({
    from: effectiveFrom,
    to: effectiveTo,
    items,
    targetLevelItemTypes: curLevelItemTypes,
    nextLevelItemTypes,
    showChildItems: layout.showChildItems ?? false,
  });

  const itemsData = useMemo(
    () => ({
      items: filteredItemsWithChildren,
      itemTypes,
      itemLevels,
      itemStatuses,
    }),
    [filteredItemsWithChildren, itemTypes, itemLevels, itemStatuses],
  );

  const timelineHeader = useMemo(
    () => headerToPropertySelector(layout.header1 ?? null),
    [layout.header1],
  );
  const timelineSubHeader = useMemo(
    () => headerToPropertySelector(layout.header2 ?? null),
    [layout.header2],
  );

  const colorContextItems = useMemo(
    () => items.filter((item) => item.plannedStartDateTime || item.plannedFinishDateTime),
    [items],
  );

  const {
    currentColorByField: contextColorByField,
    orderedHeaderValues: contextOrderedHeaderValues,
    paletteSwatches,
    valueToColorIndex,
  } = useTimelineColorContext({
    items: colorContextItems,
    itemTypes,
    itemLevels,
    itemStatuses,
    noValueLabel: t(($) => $.loopVisualization.layout.formatPanel.colors.noValue),
    isPublishedMode,
    publishedLayout: isPublishedMode ? layout : undefined,
    publishedMainHeaderConfig: isPublishedMode ? mainHeaderConfigProp : undefined,
    allItemsById,
    teamsOverride,
  });

  const timelineHeaderOptions = useVisualizationHeaderOptions(
    timelineHeader,
    items,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
      allItemsById,
    },
  ) as IVisualizationHeader;

  const timelineSubHeaderOptions = useVisualizationHeaderOptions(
    timelineSubHeader,
    items,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
      allItemsById,
    },
  ) as IVisualizationHeader;

  const showEmptyHeaders = layout.showEmptyHeaders ?? false;

  const header1FieldId = useMemo(
    () => getHeaderFieldId(timelineHeaderOptions),
    [timelineHeaderOptions],
  );
  const header2FieldId = useMemo(
    () => getHeaderFieldId(timelineSubHeaderOptions),
    [timelineSubHeaderOptions],
  );

  const includeNullParentItemHeader = useCallback(
    (headerOptions: IVisualizationHeader | null) => {
      if (headerOptions?.type !== 'ParentItemSelector') return false;
      if (showEmptyHeaders) return true;

      const parentIds = new Set(
        headerOptions.options
          .map((option) => option.id)
          .filter((id): id is number => typeof id === 'number'),
      );
      return items.some((item) => item.parentId == null || !parentIds.has(item.parentId));
    },
    [items, showEmptyHeaders],
  );

  const includeNullHeader1 = includeNullParentItemHeader(timelineHeaderOptions);
  const includeNullHeader2 = includeNullParentItemHeader(timelineSubHeaderOptions);

  const effectiveColorByField = contextColorByField || 'status';
  const orderedHeaderValues = contextOrderedHeaderValues;

  const mainHeaders = useTimelineMainHeaders({
    timelineHeaderOptions,
    colorConfig: layout.colorConfig,
    header1FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader: includeNullHeader1,
  });

  const subHeaders = useTimelineSubHeaders({
    timelineSubHeaderOptions,
    colorConfig: layout.colorConfig,
    header2FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader: includeNullHeader2,
  });

  const itemColorById = useMemo(() => {
    const colorConfig = layout.colorConfig;
    if (!colorConfig || colorConfig.mode !== 'palette') return undefined;
    if (!effectiveColorByField) return undefined;

    const result: Record<number, string> = {};
    for (const item of items) {
      const fieldValue = getItemFieldValue(item, effectiveColorByField);
      const resolvedValue = fieldValue ?? NO_VALUE_ID;
      const index = valueToColorIndex[resolvedValue];
      result[item.id] =
        index === undefined
          ? DEFAULT_FALLBACK_COLOR
          : paletteSwatches.itemColors[index % paletteSwatches.itemColors.length] ||
            DEFAULT_FALLBACK_COLOR;
    }
    return result;
  }, [
    items,
    layout.colorConfig,
    effectiveColorByField,
    paletteSwatches.itemColors,
    valueToColorIndex,
  ]);

  const { data: mainHeaderConfigQuery } = useTimelineHeaderConfig({
    viewId,
    visualizationId,
    headerFieldId: isPublishedMode ? null : header1FieldId,
  });

  const subHeaderConfigsQuery = useTimelineSubHeaderConfigs({
    viewId,
    visualizationId,
    headerFieldId: header2FieldId,
    parentFieldId: header1FieldId,
    headers: isPublishedMode ? [] : mainHeaders,
  });

  const mainHeaderConfig = isPublishedMode ? mainHeaderConfigProp : mainHeaderConfigQuery;
  const subHeaderConfigs = isPublishedMode ? subHeaderConfigsProp : subHeaderConfigsQuery;

  const handleItemDrop = useCallback(
    (
      itemId: number,
      row: TimelineRowType,
      newStartDate: string,
      newEndDate: string,
      sourceRow?: TimelineRowType,
    ) => {
      const headerFieldId = getHeaderFieldId(timelineHeaderOptions);
      const itemHeaderPropertyKey = getHeaderItemKey(headerFieldId);
      const itemHeaderValue = row.parentHeaderId ? row.parentHeaderId : row.headerId;
      const sourceHeaderValue = sourceRow
        ? sourceRow.parentHeaderId
          ? sourceRow.parentHeaderId
          : sourceRow.headerId
        : undefined;

      const subHeaderFieldId = getHeaderFieldId(timelineSubHeaderOptions);
      const itemSubHeaderPropertyKey = getHeaderItemKey(subHeaderFieldId);
      const itemSubHeaderValue = row.parentHeaderId ? row.headerId : null;
      const sourceSubHeaderValue = sourceRow?.parentHeaderId ? sourceRow.headerId : null;

      const updateInput = createEmptyUpdateInput(itemId);
      const combineMultiSelect = layout.combineMultiSelectHeaders ?? false;
      const item = items.find((i) => i.id === itemId);

      if (itemHeaderPropertyKey && itemHeaderValue !== undefined) {
        const parsedHeaderFieldId = Number(headerFieldId);
        const headerField = !Number.isNaN(parsedHeaderFieldId)
          ? customFieldsById.get(parsedHeaderFieldId)
          : undefined;
        const headerRestricted =
          !!headerField &&
          !!item &&
          !isCustomFieldApplicableToItemType(headerField, item.itemTypeId);

        if (!headerRestricted) {
          applyHeaderValueToUpdate({
            updateInput,
            propertyKey: itemHeaderPropertyKey,
            targetValue: itemHeaderValue,
            sourceValue: sourceHeaderValue,
            customFields: item?.customFieldValues,
            noValueId: NO_VALUE_ID,
            compositeIdSeparator: COMPOSITE_ID_SEPARATOR,
            combineMultiSelect,
          });
        }

        if (itemSubHeaderPropertyKey && itemSubHeaderValue !== null) {
          const parsedSubHeaderFieldId = Number(subHeaderFieldId);
          const subHeaderField = !Number.isNaN(parsedSubHeaderFieldId)
            ? customFieldsById.get(parsedSubHeaderFieldId)
            : undefined;
          const subHeaderRestricted =
            !!subHeaderField &&
            !!item &&
            !isCustomFieldApplicableToItemType(subHeaderField, item.itemTypeId);

          if (!subHeaderRestricted) {
            applyHeaderValueToUpdate({
              updateInput,
              propertyKey: itemSubHeaderPropertyKey,
              targetValue: itemSubHeaderValue,
              sourceValue: sourceSubHeaderValue,
              customFields: item?.customFieldValues,
              noValueId: NO_VALUE_ID,
              compositeIdSeparator: COMPOSITE_ID_SEPARATOR,
              combineMultiSelect,
            });
          }
        }
      }

      updateInput.itemData.plannedStartDateTime = toISOTimestamp(newStartDate);
      updateInput.itemData.plannedFinishDateTime = toISOTimestamp(newEndDate);

      updateVisualizationItem(updateInput);
    },
    [
      items,
      timelineHeaderOptions,
      timelineSubHeaderOptions,
      updateVisualizationItem,
      layout.combineMultiSelectHeaders,
      customFieldsById,
    ],
  );

  const handleItemResize = useCallback(
    (itemId: number, newStartDate: string, newEndDate: string) => {
      const updateInput = createEmptyUpdateInput(itemId);
      updateInput.itemData.plannedStartDateTime = toISOTimestamp(newStartDate);
      updateInput.itemData.plannedFinishDateTime = toISOTimestamp(newEndDate);
      updateVisualizationItem(updateInput);
    },
    [updateVisualizationItem],
  );

  const {
    deferredFrom,
    deferredTo,
    firstItemWeekStartDate,
    lastItemWeekEndDate,
    minimapStart,
    minimapEnd,
    deferredMinimapStart,
    deferredMinimapEnd,
  } = useTimelineMinimapDates({ from: effectiveFrom, to: effectiveTo, items: itemsData.items });

  const data = useTimelineTableData({
    from: deferredFrom || '',
    to: deferredTo || '',
    minimapStart: deferredMinimapStart,
    minimapEnd: deferredMinimapEnd,
    mainHeaders,
    subHeaders,
    items: filteredItemsWithChildren,
    timelineHeaderOptions,
    timelineSubHeaderOptions,
    layout,
    expandedChildren,
    mainHeaderConfig,
    subHeaderConfigs,
    itemRanks,
  });

  const deferredData = useDeferredValue(data);

  const hasNoItems = !isFetching && items.length === 0 && !hasFilters;
  const noItemsMatchFilters = !isFetching && items.length === 0 && hasFilters;
  const hasNoItemsMatchingLayout =
    !isFetching && items.length === 0 && Object.keys(itemTypeFilter).length > 0;
  const hasNoHeaders = !timelineHeader;
  const hasItemsButNoDates =
    !isFetching &&
    items.length > 0 &&
    firstItemWeekStartDate === null &&
    lastItemWeekEndDate === null;

  if (!deferredFrom || !deferredTo) return null;
  if (hasNoItems) return <VisualizationNoItemsState visualizationType="timeline" />;
  if (hasNoHeaders) return <VisualizationNoHeadersState visualizationType="timeline" />;
  if (noItemsMatchFilters) return <VisualizationNoItemsMatchFiltersState />;
  if (hasNoItemsMatchingLayout) return <VisualizationNoItemsMatchLayoutState />;
  if (hasItemsButNoDates) return <TimelineNoItemsWithDates />;

  return (
    <>
      <div className="flex flex-col max-w-full max-h-full overflow-hidden relative">
        {!isPublishedMode && (
          <TimelineMinimap
            visualizationId={visualizationId}
            viewId={viewId}
            from={deferredFrom}
            to={deferredTo}
            data={data}
            onChange={onDateChange}
            minimapStart={minimapStart}
            minimapEnd={minimapEnd}
            canEdit={canEdit}
            scrollContainerRef={scrollContainerRef}
            expandedRows={expandedRows}
            itemColorById={itemColorById}
            isCompact={layout.theme === 'compact'}
          />
        )}

        <TimelineTable
          from={deferredFrom}
          to={deferredTo}
          data={deferredData}
          canEdit={canEdit}
          isPublishedMode={isPublishedMode}
          isExportMode={isExportMode}
          onItemDrop={handleItemDrop}
          onItemResize={handleItemResize}
          header1FieldId={header1FieldId}
          header2FieldId={header2FieldId}
          scrollContainerRef={scrollContainerRef}
          layout={layout}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          itemColorById={itemColorById}
          rowHeaderWidth={rowHeaderWidth}
          onRowHeaderWidthChange={setRowHeaderWidth}
          expandedChildren={expandedChildren}
          setExpandedChildren={setExpandedChildren}
          setReorderHeadersModalState={setReorderHeadersModalState}
          onItemClick={onItemClick}
          viewId={viewId}
          visualizationId={visualizationId}
          itemRanks={itemRanks}
        />

        {isFetching && (
          <div className="absolute inset-0 bg-bg-surface-base/50 flex items-center justify-center z-10">
            <Spinner size={24} />
          </div>
        )}

        {!isPublishedMode && canEdit && (
          <TimelineAddHeaderMenu
            header1FieldId={header1FieldId}
            header2FieldId={header2FieldId}
            allMainHeaders={mainHeaders}
            allSubHeaders={subHeaders}
            subHeaderConfigs={subHeaderConfigs}
          />
        )}

        {isPublishedMode && !hideLegendOverlay && publishedLegendItems?.length ? (
          <div className="absolute bottom-4 right-4 z-10">
            <Legend items={publishedLegendItems} />
          </div>
        ) : null}
      </div>

      {reorderHeadersModalState && (
        <ReorderTimelineHeadersDialog
          isOpen={true}
          onClose={() => setReorderHeadersModalState(null)}
          headers={reorderHeadersModalState.isReorderingMainHeader ? mainHeaders : subHeaders}
          headerFieldId={
            reorderHeadersModalState.isReorderingMainHeader ? header1FieldId : header2FieldId
          }
          parentHeaderId={reorderHeadersModalState.parentHeaderId}
          parentFieldId={!reorderHeadersModalState.isReorderingMainHeader ? header1FieldId : null}
          visualizationId={visualizationId}
        />
      )}
    </>
  );
};
